import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import BetAmountModal from "./BetAmountModal";
import RouletteBoard from "./RouletteBoard";
import RouletteCroupierPanel from "./RouletteCroupierPanel";
import RouletteLatestPanel from "./RouletteLatestPanel";

function RouletteBetModal({
  bets,
  balance,
  draftTotalStake,
  availableBalance,
  readOnly,
  selectedRegion,
  locking,
  latestResults,
  croupierImage,
  hideDesktopCroupierImage,
  onSelectRegion,
  onAmountConfirm,
  onAmountClose,
  onLock,
  onClearDrafts,
  onBack,
}) {
  const layerRef = useRef(null);
  const modalRef = useRef(null);
  const [tableScale, setTableScale] = useState(1);

  const fitTableToViewport = useCallback(() => {
    const layer = layerRef.current;
    const modal = modalRef.current;

    if (!layer || !modal) {
      return;
    }

    const layerStyles = window.getComputedStyle(layer);
    const horizontalPadding =
      parseFloat(layerStyles.paddingLeft || "0") +
      parseFloat(layerStyles.paddingRight || "0");
    const verticalPadding =
      parseFloat(layerStyles.paddingTop || "0") +
      parseFloat(layerStyles.paddingBottom || "0");

    const availableWidth = Math.max(
      1,
      layer.clientWidth - horizontalPadding
    );
    const availableHeight = Math.max(
      1,
      layer.clientHeight - verticalPadding
    );

    const naturalWidth = modal.offsetWidth;
    const naturalHeight = modal.scrollHeight;

    const nextScale = Math.min(
      1,
      availableWidth / naturalWidth,
      availableHeight / naturalHeight
    );

    setTableScale((currentScale) =>
      Math.abs(currentScale - nextScale) > 0.001
        ? nextScale
        : currentScale
    );
  }, []);

  useLayoutEffect(() => {
    fitTableToViewport();

    const modal = modalRef.current;
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(fitTableToViewport)
        : null;

    if (modal && resizeObserver) {
      resizeObserver.observe(modal);
    }

    window.addEventListener("resize", fitTableToViewport);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", fitTableToViewport);
    };
  }, [fitTableToViewport, readOnly]);

  const croupierProps = {
    bets,
    balance,
    draftTotalStake,
    availableBalance,
    readOnly,
    locking,
    latestResults,
    croupierImage,
    onLock,
    onClearDrafts,
    onBack,
  };

  return (
    <div
      ref={layerRef}
      className="casino-modal-layer roulette-bet-layer"
      role="presentation"
      style={{
        "--roulette-table-visual-width": `${465 * tableScale}px`,
      }}
    >
      <div
        className="roulette-desktop-croupier-slot"
        aria-hidden="false"
      >
        <RouletteCroupierPanel
          {...croupierProps}
          variant="desktop"
          hideFigure={hideDesktopCroupierImage}
        />
      </div>

      <div
        className="roulette-desktop-latest-slot"
        aria-hidden="false"
      >
        <RouletteLatestPanel latestResults={latestResults} />
      </div>

      <section
        ref={modalRef}
        className="roulette-bet-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Roulette bets"
        style={{
          "--roulette-table-fit-scale": tableScale,
        }}
      >
        <div className="roulette-bet-layout">
          <RouletteCroupierPanel
            {...croupierProps}
            variant="drawer"
          />

          <div className="roulette-table-panel">
            {readOnly && (
              <p className="roulette-readonly-message">
                BET STATE LOCKED — WAGERS ARE READ-ONLY UNTIL THIS SPIN IS SETTLED.
              </p>
            )}

            <RouletteBoard
              bets={bets}
              readOnly={readOnly}
              selectedRegion={selectedRegion}
              onSelectRegion={onSelectRegion}
            />
          </div>
        </div>
      </section>

      {selectedRegion && !readOnly && (
        <BetAmountModal
          region={selectedRegion}
          availableBalance={availableBalance}
          onConfirm={onAmountConfirm}
          onClose={onAmountClose}
        />
      )}
    </div>
  );
}

export default RouletteBetModal;
